import { useEffect } from "react";

import { Grid2, InputAdornment, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import { useTeachingPolicy } from "../../../../../hooks/useTeachingPolicy";
import { validators } from "../../../../../utils";

export function RescheduleFee() {
	const { policy, savePolicy } = useTeachingPolicy();

	const defaultValues = {
		rescheduleFee: `${policy?.rescheduleFee ?? ""}`,
		minAppointmentDuration: `${policy?.minAppointmentDuration ?? ""}`,
	};

	const { control, setValue, trigger, getValues } = useForm({
		defaultValues,
		mode: "all",
	});

	useEffect(() => {
		if (policy) {
			setValue("rescheduleFee", `${policy.rescheduleFee}`);
			setValue("minAppointmentDuration", `${policy.minAppointmentDuration}`);
		}
	}, [policy, setValue]);

	const saveRescheduleFee = async () => {
		const isRescheduleFeeValid = await trigger("rescheduleFee");
		const isMinAppointmentDurationValid = await trigger(
			"minAppointmentDuration",
		);

		if (!isRescheduleFeeValid || !isMinAppointmentDurationValid) {
			return;
		}

		const rescheduleFee = getValues("rescheduleFee");
		const minAppointmentDuration = getValues("minAppointmentDuration");

		if (
			rescheduleFee === `${policy?.rescheduleFee ?? ""}` &&
			minAppointmentDuration === `${policy?.minAppointmentDuration ?? ""}`
		) {
			return;
		}

		savePolicy({
			rescheduleFee: Number(rescheduleFee),
			minAppointmentDuration: Number(minAppointmentDuration),
		});
	};

	return (
		<div className="section-separator">
			<Typography className="small-title-text bold">
				Rescheduling fees
			</Typography>
			<Typography className="normal-text item-separator-tiny">
				You can decide if a late rescheduling fee will apply to bookings shifted
				by students.
			</Typography>
			<div className="item-separator-small" />
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
					<Typography className="normal-text">
						Day(s) prior to appointment
					</Typography>
					<Controller
						control={control}
						name="minAppointmentDuration"
						rules={{
							required: {
								value: true,
								message: "Please enter an integer",
							},
							pattern: {
								value: validators.price,
								message: "Please enter an integer",
							},
						}}
						render={({ field: { onChange, value }, formState: { errors } }) => {
							const errorMessage = errors.minAppointmentDuration?.message;
							let className = "";
							if (errorMessage) {
								className += " invalid";
							}

							return (
								<div className="common-input item-separator-tiny">
									<TextField
										value={value}
										onChange={(e) => {
											if (
												e.target.value === "" ||
												validators.price.test(e.target.value)
											) {
												onChange(e);
											}
										}}
										fullWidth
										className={className}
										slotProps={{
											input: {
												classes: {
													input: "reschedule-fee-input",
												},
												endAdornment: (
													<InputAdornment position="end">
														<Typography className="normal-text">
															day(s)
														</Typography>
													</InputAdornment>
												),
												onBlur: saveRescheduleFee,
											},
										}}
									/>
									{errorMessage && (
										<Typography className="invalid-message">
											{errorMessage}
										</Typography>
									)}
								</div>
							);
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
					<Typography className="normal-text">Fee (% of Class Cost)</Typography>
					<Controller
						control={control}
						name="rescheduleFee"
						rules={{
							required: {
								value: true,
								message: "Please enter an integer from 0 to 100",
							},
							pattern: {
								value: validators.price,
								message: "Please enter an integer from 0 to 100",
							},
							min: {
								value: 0,
								message: "Please enter an integer from 0 to 100",
							},
							max: {
								value: 100,
								message: "Please enter an integer from 0 to 100",
							},
						}}
						render={({ field: { onChange, value }, formState: { errors } }) => {
							const errorMessage = errors.rescheduleFee?.message;
							let className = "";
							if (errorMessage) {
								className += " invalid";
							}

							return (
								<div className="common-input item-separator-tiny">
									<TextField
										value={value}
										onChange={(e) => {
											if (
												e.target.value === "" ||
												(validators.price.test(e.target.value) &&
													Number(e.target.value) <= 100)
											) {
												onChange(e);
											}
										}}
										fullWidth
										className={className}
										slotProps={{
											input: {
												classes: {
													input: "reschedule-fee-input",
												},
												endAdornment: (
													<InputAdornment position="end">
														<Typography className="normal-text">%</Typography>
													</InputAdornment>
												),
												onBlur: saveRescheduleFee,
												onKeyDown(e) {
													if (e.key === "Enter") {
														saveRescheduleFee();
													}
												},
											},
										}}
									/>
									{errorMessage && (
										<Typography className="invalid-message">
											{errorMessage}
										</Typography>
									)}
								</div>
							);
						}}
					/>
				</Grid2>
			</Grid2>
		</div>
	);
}
